import React, { useState, useEffect } from "react";
import { db } from "@/api/db";
import { Video, Calendar as CalendarIcon, ArrowLeft, Clock, Search, X, Play, User } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ptBR } from "date-fns/locale";
import { STATUS_CONFIG, CATEGORIES, CATEGORY_COLORS } from "@/lib/live-constants";

function getEmbedUrl(url) {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|live\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  const loomMatch = url.match(/loom\.com\/share\/([a-zA-Z0-9]+)/);
  if (loomMatch) return `https://www.loom.com/embed/${loomMatch[1]}`;
  return url;
}

export default function Recordings() {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");
  const [dateRange, setDateRange] = useState(undefined);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [playerLesson, setPlayerLesson] = useState(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    const data = await db.entities.Lesson.filter({ type: "live", is_published: true }, "-live_date");
    setLessons(data.filter(l => l.live_status === "recording"));
    setLoading(false);
  }

  function handleRangeSelect(range) {
    setDateRange(range);
    if (range?.from && range?.to) setCalendarOpen(false);
  }

  function clearFilters() {
    setSearch("");
    setActiveCategory("all");
    setDateRange(undefined);
  }

  const hasFilters = search || activeCategory !== "all" || dateRange?.from;

  const filtered = lessons.filter(l => {
    const matchCategory = activeCategory === "all" || l.category === activeCategory;
    const matchSearch = !search ||
      l.title?.toLowerCase().includes(search.toLowerCase()) ||
      l.description?.toLowerCase().includes(search.toLowerCase());
    const lessonDate = l.live_date ? new Date(l.live_date) : null;
    const matchDate = !dateRange?.from || (
      lessonDate && lessonDate >= dateRange.from &&
      (!dateRange.to || lessonDate <= new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59))
    );
    return matchCategory && matchSearch && matchDate;
  });

  // ── Player view ──────────────────────────────────────────────────
  if (playerLesson) {
    const sc = STATUS_CONFIG.recording;
    const catColor = CATEGORY_COLORS[playerLesson.category] || { bg: "#F2F4F4", text: "#646C6F" };
    const activeUrl = playerLesson.video_url || playerLesson.live_url;
    const embedUrl = getEmbedUrl(activeUrl);

    return (
      <div className="max-w-4xl mx-auto space-y-5">
        <button
          onClick={() => setPlayerLesson(null)}
          className="flex items-center gap-1.5 text-sm font-medium"
          style={{ color: "#646C6F" }}
        >
          <ArrowLeft className="w-4 h-4" /> Voltar para gravações
        </button>

        <div className="rounded-2xl overflow-hidden shadow-md" style={{ background: "#1A1D1E" }}>
          <div className="px-4 py-3 flex items-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold flex-shrink-0"
              style={{ background: sc.bg, color: sc.text }}
            >
              {sc.label}
            </span>
            {playerLesson.category && (
              <span
                className="px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0"
                style={{ background: catColor.bg, color: catColor.text }}
              >
                {playerLesson.category}
              </span>
            )}
            <p className="text-white text-sm font-medium truncate">{playerLesson.title}</p>
          </div>

          {embedUrl ? (
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                key={embedUrl}
                src={embedUrl}
                title={playerLesson.title}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#FEEDB4" }}>
                <Clock className="w-8 h-8" style={{ color: "#65541B" }} />
              </div>
              <p className="text-sm" style={{ color: "#99A6AB" }}>
                Gravação ainda não disponível.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-2xl p-5 space-y-2" style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
          <h2 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#2E3233" }}>
            {playerLesson.title}
          </h2>
          {playerLesson.live_date && (
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#99A6AB" }} />
              <span className="text-sm" style={{ color: "#646C6F" }}>
                {new Date(playerLesson.live_date).toLocaleString("pt-BR", {
                  day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </span>
            </div>
          )}
          {playerLesson.description && (
            <p className="text-sm leading-relaxed" style={{ color: "#646C6F" }}>
              {playerLesson.description}
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── Listagem ─────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-6">

      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
          Gravações
        </h1>
        <p className="text-sm mt-0.5" style={{ color: "#99A6AB" }}>
          Assista às gravações dos encontros que já aconteceram.
        </p>
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "#99A6AB" }} />
          <input
            type="text"
            placeholder="Buscar gravações..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-full text-sm focus:outline-none"
            style={{ border: "1px solid #E5E9EA", background: "#fff", color: "#2E3233" }}
            onFocus={e => e.target.style.borderColor = "#00DCA4"}
            onBlur={e => e.target.style.borderColor = "#E5E9EA"}
          />
        </div>

        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <button
              className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold transition-all flex-shrink-0"
              style={dateRange?.from
                ? { background: "#EAFAF6", color: "#00664C", border: "2px solid #00DCA4" }
                : { background: "#fff", color: "#646C6F", border: "1px solid #E5E9EA" }}
            >
              <CalendarIcon className="w-4 h-4 flex-shrink-0" />
              {dateRange?.from ? (
                dateRange.to
                  ? `${dateRange.from.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} → ${dateRange.to.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })}`
                  : dateRange.from.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })
              ) : "Filtrar por período"}
              {dateRange?.from && (
                <span
                  onClick={e => { e.stopPropagation(); setDateRange(undefined); }}
                  className="w-4 h-4 rounded-full flex items-center justify-center ml-0.5"
                  style={{ background: "#00664C", color: "#fff" }}
                >
                  <X className="w-2.5 h-2.5" />
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleRangeSelect}
              locale={ptBR}
              numberOfMonths={2}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setActiveCategory("all")}
          className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
          style={activeCategory === "all"
            ? { background: "#003326", color: "#fff" }
            : { background: "#F2F4F4", color: "#646C6F" }}
        >
          Todas as categorias
        </button>
        {CATEGORIES.map(cat => {
          const active = activeCategory === cat;
          const cc = CATEGORY_COLORS[cat];
          return (
            <button
              key={cat}
              onClick={() => setActiveCategory(active ? "all" : cat)}
              className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={active
                ? { background: cc.bg, color: cc.text, outline: "2px solid #00DCA4", outlineOffset: "2px" }
                : { background: "#F2F4F4", color: "#646C6F" }}
            >
              {cat}
            </button>
          );
        })}
      </div>

      {!loading && (
        <div className="flex items-center justify-between">
          <p className="text-sm" style={{ color: "#99A6AB" }}>
            {filtered.length} {filtered.length === 1 ? "gravação encontrada" : "gravações encontradas"}
          </p>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs font-semibold"
              style={{ color: "#00664C" }}
            >
              <X className="w-3 h-3" /> Limpar filtros
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl p-16 flex flex-col items-center gap-3 text-center"
          style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
          <Video className="w-10 h-10 opacity-20" style={{ color: "#00664C" }} />
          <p className="font-medium" style={{ color: "#99A6AB" }}>Nenhuma gravação encontrada para esses filtros.</p>
          <button
            onClick={clearFilters}
            className="text-xs font-semibold px-4 py-2 rounded-full"
            style={{ background: "#EAFAF6", color: "#00664C" }}
          >
            Limpar filtros
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(lesson => {
            const catColor = CATEGORY_COLORS[lesson.category] || null;
            const d = lesson.live_date ? new Date(lesson.live_date) : null;
            const dateStr = d ? d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" }) : null;
            return (
              <div
                key={lesson.id}
                className="rounded-2xl bg-white cursor-pointer transition-all hover:shadow-md"
                style={{ border: "1px solid #E5E9EA" }}
                onClick={() => setPlayerLesson(lesson)}
              >
                <div className="flex items-center gap-4 p-4">
                  {/* Thumbnail pequena ou ícone */}
                  <div className="relative flex-shrink-0">
                    {lesson.image_url ? (
                      <div className="w-20 h-14 rounded-xl overflow-hidden relative">
                        <img src={lesson.image_url} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.3)" }}>
                          <Play className="w-5 h-5 text-white" fill="white" />
                        </div>
                      </div>
                    ) : (
                      <div
                        className="w-20 h-14 rounded-xl flex items-center justify-center"
                        style={{ background: "#EAFAF6" }}
                      >
                        <Play className="w-5 h-5" style={{ color: "#00664C" }} fill="#00664C" />
                      </div>
                    )}
                  </div>

                  {/* Conteúdo principal */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {catColor && lesson.category && (
                        <span
                          className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                          style={{ background: catColor.bg, color: catColor.text }}
                        >
                          {lesson.category}
                        </span>
                      )}
                    </div>
                    <h3
                      className="font-semibold text-sm line-clamp-1"
                      style={{ color: "#2E3233" }}
                      title={lesson.title}
                    >
                      {lesson.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      {dateStr && (
                        <span className="flex items-center gap-1 text-xs" style={{ color: "#99A6AB" }}>
                          <CalendarIcon className="w-3 h-3" /> {dateStr}
                        </span>
                      )}
                      {lesson.presenter && (
                        <span className="flex items-center gap-1 text-xs" style={{ color: "#99A6AB" }}>
                          <User className="w-3 h-3" /> {lesson.presenter}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Botão assistir */}
                  <div className="flex-shrink-0">
                    <span
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold"
                      style={{ background: "#EAFAF6", color: "#00664C" }}
                    >
                      <Play className="w-3 h-3" fill="#00664C" /> Assistir
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
